import {useCallback, useState} from 'react';
import {SaveState} from '../shared/saveState';
import {NovaPromptState, PromptInputs, useNovaPromptState} from './useNovaPromptState';

export interface NovaAgentSavePayload extends PromptInputs {
	categoryId: string | null;
	title: string;
	description: string;
}

export interface UseNovaAgentDraftOptions {
	initialValues?: Partial<PromptInputs>;
	initialCategoryId?: string | null;
	initialTitle?: string;
	initialDescription?: string;
	onSave?: (values: NovaAgentSavePayload) => Promise<void>;
}

export interface NovaAgentDraft {
	promptState: NovaPromptState;
	categoryId: string | null;
	setCategoryId: (categoryId: string | null) => void;
	// The agent's display title and description are edited inline on the Prompt tab and committed
	// as part of the same working draft — there is no separate edit surface.
	title: string;
	setTitle: (title: string) => void;
	description: string;
	setDescription: (description: string) => void;
	isDirty: boolean;
	saveState: SaveState;
	save: () => Promise<void>;
	load: (inputs: PromptInputs, categoryId: string | null, title: string, description: string) => void;
}

// The Prompt tab's editors, the title/description fields, and the category picker each own one
// slice of a single working draft (the prompt inputs vs. the agent's metadata vs. which category
// it belongs to) but must be saved together as one record and reported as one save-state pill in
// the header. This composes the prompt-editing hook with the metadata + category slices on top, so
// the rest of the shell only has to deal with one draft.
function combineSaveState(promptSaveState: SaveState, metaDirty: boolean): SaveState {
	// Saving/Error already reflect the in-flight or failed save of the whole record (prompt inputs,
	// metadata and category are submitted together — see `save` below) — metadata dirtiness can't
	// change that verdict, only whether a *clean* state is `Saved` or `Unsaved`.
	if (promptSaveState === SaveState.Saving || promptSaveState === SaveState.Error) {
		return promptSaveState;
	}
	return promptSaveState === SaveState.Unsaved || metaDirty ? SaveState.Unsaved : SaveState.Saved;
}

export function useNovaAgentDraft(options: UseNovaAgentDraftOptions = {}): NovaAgentDraft {
	const {onSave, initialValues, initialCategoryId = null, initialTitle = '', initialDescription = ''} = options;

	const [categoryId, setCategoryId] = useState<string | null>(initialCategoryId);
	const [baselineCategoryId, setBaselineCategoryId] = useState<string | null>(initialCategoryId);
	const [title, setTitle] = useState<string>(initialTitle);
	const [baselineTitle, setBaselineTitle] = useState<string>(initialTitle);
	const [description, setDescription] = useState<string>(initialDescription);
	const [baselineDescription, setBaselineDescription] = useState<string>(initialDescription);

	// `onSave` is recreated whenever the metadata slices change so the merged payload always carries
	// the values as of the moment `save()` is called, not stale ones captured at mount.
	const mergedOnSave = useCallback(
		async (values: PromptInputs) => {
			if (!onSave) {
				throw new Error('useNovaAgentDraft: no onSave provided');
			}
			await onSave({...values, categoryId, title, description});
		},
		[onSave, categoryId, title, description]
	);

	const promptState = useNovaPromptState({initialValues, onSave: mergedOnSave});

	const metaDirty = categoryId !== baselineCategoryId || title !== baselineTitle || description !== baselineDescription;
	const isDirty = promptState.isDirty || metaDirty;
	const saveState = combineSaveState(promptState.saveState, metaDirty);

	const save = useCallback(async () => {
		await promptState.save();
		// The prompt-state hook already reset its own baseline on success; only the metadata
		// baselines are ours to advance.
		setBaselineCategoryId(categoryId);
		setBaselineTitle(title);
		setBaselineDescription(description);
	}, [promptState, categoryId, title, description]);

	const load = useCallback(
		(inputs: PromptInputs, newCategoryId: string | null, newTitle: string, newDescription: string) => {
			promptState.load(inputs);
			setCategoryId(newCategoryId);
			setBaselineCategoryId(newCategoryId);
			setTitle(newTitle);
			setBaselineTitle(newTitle);
			setDescription(newDescription);
			setBaselineDescription(newDescription);
		},
		[promptState]
	);

	return {
		promptState,
		categoryId,
		setCategoryId,
		title,
		setTitle,
		description,
		setDescription,
		isDirty,
		saveState,
		save,
		load,
	};
}
